import {
  nord,
  errorTextColor,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/shared/codeBlockStyles';
import { TabType } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/types';
import { twMerge } from 'tailwind-merge';
import { useState } from 'react';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { CodeBlockCopyButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/shared/CodeBlockCopyButton';

interface CodeBlockExecutionOutputProps {
  className?: string;
  outputValue: string;
  setOutputTabOpen: (value: boolean) => void;
  tab: TabType;
  setTab: (value: TabType) => void;
  fetchError: boolean;
  fullScreenEnabled: boolean;
  fullWidthEnabled: boolean;
}

const EXECUTION_SUBMISSION_ERROR =
  'Error executing code - this usually occurs due to execution timeout. Please try again.';

const NUM_LINES_COLLAPSED = 5;

function CodeBlockExecutionOutput({
  outputValue,
  setOutputTabOpen,
  tab,
  setTab,
  fetchError,
}: CodeBlockExecutionOutputProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const numLines = outputValue.split(/\r\n|\r|\n/).length;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <div
        className={twMerge(
          'group bg-transparent relative px-0 py-0',
          isExpanded ? 'overflow-auto' : 'overflow-hidden',
        )}
      >
        <div className="text-white text-xs font-mono font-medium px-3 flex items-center justify-between bg-neutral-800 rounded-t-md py-1.5">
          <Tabs
            className="border-none"
            value={tab}
            tabs={[
              {
                id: TabType.STDOUT,
                label: 'Output',
              },
              {
                id: TabType.STDERR,
                label: 'Errors',
              },
            ]}
            onChange={tabId => setTab(tabId as TabType)}
          />
          <div className="flex items-center justify-end">
            {numLines > NUM_LINES_COLLAPSED && isExpanded && (
              <button
                onClick={e => toggleExpand(e)}
                className="border ml-1 rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
              >
                Collapse
                <FontAwesomeIcon icon="chevron-up" className="ml-1" />
              </button>
            )}
            <div className="pl-4">
              <CodeBlockCopyButton code={outputValue} />
            </div>
            <button
              onClick={() => setOutputTabOpen(false)}
              className="ml-1 rounded-md py-1.5 pl-2 pr-1 text-xs flex items-center justify-center text-neutral-0"
            >
              <XIcon width={16} height={16} color="#FFFFFF" />
            </button>
          </div>
        </div>
        <div>
          <div
            className={twMerge(
              'p-3 bg-neutral-900',
              isExpanded
                ? 'max-h-[600px] overflow-auto rounded-b-md'
                : 'max-h-[97px] overflow-hidden',
              numLines <= NUM_LINES_COLLAPSED || isExpanded ? 'rounded-b-md' : 'rounded-none',
            )}
          >
            <SyntaxHighlighter
              // eslint-disable-next-line react/no-children-prop
              children={fetchError ? EXECUTION_SUBMISSION_ERROR : outputValue}
              // @ts-expect-error TODO: check how to type this - React.CCSProperties is the type specified in the library but it doesn't work
              style={tab === TabType.STDERR ? { ...nord, ...errorTextColor } : nord}
              // language={languageMatch ? languageMatch[1] : undefined}
              PreTag="div"
              CodeTag="div"
              showLineNumbers={false}
            />
          </div>
          {numLines > NUM_LINES_COLLAPSED && !isExpanded && (
            <div className="flex items-center justify-center py-2 text-white text-xs font-medium font-mono bg-neutral-900 rounded-b-md">
              <button
                onClick={e => toggleExpand(e)}
                className="border ml-1 text-white rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
              >
                Expand
                <FontAwesomeIcon icon="chevron-down" className="ml-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

type TabsProps = {
  tabs: Tab[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

type Tab = {
  id: string;
  label?: string;
  icon?: FontAwesomeIconProps['icon'];
  disabled?: boolean;
  suffix?: string;
};

/**
 * Copied from ScaleUI but with customization
 */
const Tabs = ({ tabs, value, onChange, className }: TabsProps) => {
  return (
    <div
      className={twMerge('border-0 border-b border-solid scaleui border-neutral-200', className)}
    >
      <nav className="flex -mb-px space-x-4" aria-label="Tabs">
        {tabs.map(tab => {
          const label = tab.label ?? tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              disabled={tab.disabled}
              className={twMerge(
                'whitespace-nowrap py-2 px-1 border-0 border-solid border-b-2 font-medium text-xs text-neutral-0',
                tab.id === value
                  ? 'border-neutral-0'
                  : 'border-transparent hover:border-primary-500',
              )}
              aria-current={tab.id === value ? 'page' : undefined}
              onClick={() => onChange(tab.id)}
            >
              <span className="flex items-center">
                {tab.icon && <FontAwesomeIcon icon={tab.icon} className="mr-2" />}
                {label}
                {tab.suffix && <span className="ml-1">{tab.suffix}</span>}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

const XIcon = ({ width, height, color }: { width: number; height: number; color: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default CodeBlockExecutionOutput;
